import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Stack,
  InputAdornment,
  IconButton,
} from '@mui/material'
import {
  Visibility,
  VisibilityOff,
  CheckCircle,
  Error as ErrorIcon,
  LockOutlined,
  Person,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { authService } from '../services/authService'
import { useAuth } from '../contexts/AuthContext'
import { Language } from '../types/auth'

// 言語設定をi18n形式に変換するヘルパー関数
const mapLanguageToI18n = (language?: Language | null): string => {
  if (!language) return 'ja'
  return language === 'EN' ? 'en' : 'ja'
}

const InvitationAcceptPage: React.FC = () => {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { i18n } = useTranslation()
  const { setUser, setToken: setAuthToken, refreshAccessibleFeatures } = useAuth()

  // 検証状態
  const [verifying, setVerifying] = useState(true)
  const [isValid, setIsValid] = useState(false)
  const [userData, setUserData] = useState<{ name: string; email: string } | null>(null)
  const [expiresAt, setExpiresAt] = useState<string | null>(null)
  const [verifyError, setVerifyError] = useState<string>('')

  // パスワード入力状態
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  // 送信状態
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string>('')
  const [success, setSuccess] = useState(false)

  // パスワードバリデーション
  const passwordValidation = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
    matches: password === confirmPassword && password.length > 0,
  }

  const isPasswordValid =
    passwordValidation.minLength &&
    passwordValidation.hasUppercase &&
    passwordValidation.hasLowercase &&
    passwordValidation.hasNumber &&
    passwordValidation.matches

  // トークン検証
  const verifyToken = useCallback(async () => {
    if (!token) {
      setVerifyError('招待リンクが無効です')
      setVerifying(false)
      return
    }

    try {
      const result = await authService.verifyInvitation(token)

      if (result.valid && result.user) {
        setIsValid(true)
        setUserData(result.user)
        setExpiresAt(result.expiresAt || null)
      } else {
        setIsValid(false)
        setVerifyError(result.error || '招待リンクが無効です')
      }
    } catch (error) {
      setIsValid(false)
      setVerifyError('招待リンクの検証中にエラーが発生しました')
    } finally {
      setVerifying(false)
    }
  }, [token])

  useEffect(() => {
    verifyToken()
  }, [verifyToken])

  // パスワード設定送信
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!token || !isPasswordValid) return

    setSubmitting(true)
    setSubmitError('')

    try {
      const response = await authService.acceptInvitation(token, password)

      if (response.token && response.user) {
        setSuccess(true)
        setAuthToken(response.token)
        setUser(response.user)

        // ユーザーの言語設定に合わせてi18nを同期
        const userLanguage = mapLanguageToI18n(response.user.preferredLanguage)
        if (i18n.language !== userLanguage) {
          await i18n.changeLanguage(userLanguage)
          console.log('🌐 言語設定を同期:', userLanguage)
        }

        // アクセス可能な機能を取得（メニュー表示に必要）
        await refreshAccessibleFeatures(response.user.id)

        // 3秒後にダッシュボードへ遷移
        setTimeout(() => {
          navigate('/dashboard')
        }, 3000)
      }
    } catch (error: any) {
      setSubmitError(
        error.response?.data?.error || 'アカウント設定に失敗しました。もう一度お試しください。'
      )
    } finally {
      setSubmitting(false)
    }
  }

  // ローディング中
  if (verifying) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="h6">招待リンクを確認中...</Typography>
          </Paper>
        </Box>
      </Container>
    )
  }

  // 無効なトークン
  if (!isValid) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
            <ErrorIcon color="error" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h5" gutterBottom color="error">
              招待リンクが無効です
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              {verifyError}
            </Typography>
            <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
              <Typography variant="body2">
                この問題が続く場合は、管理者に新しい招待リンクの発行を依頼してください。
              </Typography>
            </Alert>
            <Button variant="contained" onClick={() => navigate('/login')}>
              ログインページへ
            </Button>
          </Paper>
        </Box>
      </Container>
    )
  }

  // 成功
  if (success) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircle color="primary" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h5" gutterBottom color="primary.main">
              アカウント設定完了
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              パスワードの設定が完了しました。
            </Typography>
            <Typography variant="body2" color="text.secondary">
              まもなくダッシュボードに移動します...
            </Typography>
            <CircularProgress size={24} sx={{ mt: 2 }} />
          </Paper>
        </Box>
      </Container>
    )
  }

  // パスワード設定フォーム
  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4,
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Stack spacing={3}>
            <Box sx={{ textAlign: 'center' }}>
              <LockOutlined color="primary" sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="h5" gutterBottom>
                アカウント設定
              </Typography>
              <Typography color="text.secondary">
                パスワードを設定してアカウントを有効化してください
              </Typography>
            </Box>

            {/* ユーザー情報表示 */}
            {userData && (
              <Alert severity="info" icon={<Person />}>
                <Typography variant="body2">
                  <strong>{userData.name}</strong> 様 ({userData.email})
                </Typography>
              </Alert>
            )}

            {submitError && (
              <Alert severity="error" onClose={() => setSubmitError('')}>
                {submitError}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <TextField
                  label="新しいパスワード"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  fullWidth
                  required
                  autoFocus
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  label="パスワード確認"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  fullWidth
                  required
                  error={confirmPassword.length > 0 && !passwordValidation.matches}
                  helperText={
                    confirmPassword.length > 0 && !passwordValidation.matches
                      ? 'パスワードが一致しません'
                      : ''
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* パスワード要件 */}
                <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    パスワード要件:
                  </Typography>
                  <Stack spacing={0.5}>
                    <Typography
                      variant="body2"
                      color={passwordValidation.minLength ? 'primary.main' : 'text.secondary'}
                    >
                      {passwordValidation.minLength ? '✓' : '○'} 8文字以上
                    </Typography>
                    <Typography
                      variant="body2"
                      color={passwordValidation.hasUppercase ? 'primary.main' : 'text.secondary'}
                    >
                      {passwordValidation.hasUppercase ? '✓' : '○'} 大文字を含む
                    </Typography>
                    <Typography
                      variant="body2"
                      color={passwordValidation.hasLowercase ? 'primary.main' : 'text.secondary'}
                    >
                      {passwordValidation.hasLowercase ? '✓' : '○'} 小文字を含む
                    </Typography>
                    <Typography
                      variant="body2"
                      color={passwordValidation.hasNumber ? 'primary.main' : 'text.secondary'}
                    >
                      {passwordValidation.hasNumber ? '✓' : '○'} 数字を含む
                    </Typography>
                  </Stack>
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={!isPasswordValid || submitting}
                  sx={{ py: 1.5 }}
                >
                  {submitting ? <CircularProgress size={24} /> : 'パスワードを設定してログイン'}
                </Button>
              </Stack>
            </form>

            {expiresAt && (
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                このリンクの有効期限: {new Date(expiresAt).toLocaleString('ja-JP')}
              </Typography>
            )}
          </Stack>
        </Paper>
      </Box>
    </Container>
  )
}

export default InvitationAcceptPage
